/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import { getLogoTitle } from '../../helpers/serviceDeskHelpers';
import {
  PHONE_CHANNEL_TYPE,
  TWILIO_BYOSD_TYPE,
  WEBCHAT_CHANNEL_TYPE
} from '../../util/channelTypes';

import './TwilioIcon.scss';

/**
 * A component to render a red Twilio Icon -- color can be overridden as "fill" prop.
 * @param {integer} props.size scales the result to a size x size square, default 32
 * @param {Object} props the properties for this component
 * @returns {Object} the JSX for this component
 */
export const TwilioIcon = ({ className, channelType, ...others }) => {
  const intl = useIntl();
  let pxSize = '48px';
  let viewBox = '150 150 1000 600';

  if (channelType === WEBCHAT_CHANNEL_TYPE) {
    viewBox = '0 0 1200 600';
  }

  if (channelType === PHONE_CHANNEL_TYPE) {
    pxSize = '24px';
    viewBox = '150 150 1000 300';
  }
  return (
    <svg
      className={cx('TwilioIcon', className)}
      viewBox={viewBox}
      height={pxSize}
      fillRule="nonzero"
      xmlns="http://www.w3.org/2000/svg"
      {...others}>
      <title id="twilio-title">{getLogoTitle(TWILIO_BYOSD_TYPE, intl)}</title>
      <path
        d="M851.81573,208.71081H800.75634a2.68252,2.68252,0,0,0-2.67466,2.67466v30.71574a2.68945,2.68945,0,0,0,2.67466,2.67465h51.05939a2.6895,2.6895,0,0,0,2.67465-2.67465V211.38547a2.68257,2.68257,0,0,0-2.67465-2.67466"
        className="twilio"
      />
      <path
        d="M571.83305,346.24471c-8.96876,0-13.673-3.73381-13.673-13.92962V297.99383H586.822a2.68252,2.68252,0,0,0,2.67466-2.67465v-32.235l37.68026,125.6122a3.81787,3.81787,0,0,0,3.43668,2.55706h41.65566a3.9141,3.9141,0,0,0,3.49-2.54584l17.84518-55.64358,9.07267,29.08948,8.27945,26.54935a3.87258,3.87258,0,0,0,3.46713,2.55059h41.9176a3.83219,3.83219,0,0,0,3.44385-2.55342l38.249-125.96911V388.63238a2.67466,2.67466,0,0,0,2.67466,2.67465h51.10707a2.68251,2.68251,0,0,0,2.67465-2.67465V257.14657a2.68251,2.68251,0,0,0-2.67465-2.67465H749.98634a3.65624,3.65624,0,0,0-3.348,2.58617L733.57475,307.0985l-.685,2.578-15.88733-53.13963a3.82844,3.82844,0,0,0-3.44776-2.56776H673.88673a3.80753,3.80753,0,0,0-3.43149,2.55972L655.5605,306.62769l-.75943,2.55712-.653-2.58912-6.31182-24.76733-6.30264-24.77045a3.62241,3.62241,0,0,0-3.32638-2.586H558.16006v-42.96a2.67473,2.67473,0,0,0-3.47066-2.55356l-49.79188,15.51986a4.54686,4.54686,0,0,0-3.19384,4.34089v25.65279H488.45839a2.68251,2.68251,0,0,0-2.67465,2.67465v38.17261a2.68251,2.68251,0,0,0,2.67465,2.67465h13.24529v46.75292c0,33.07992,17.909,48.49706,51.235,48.49706,12.86984,0,25.52174-2.64512,34.43-6.89937a2.914,2.914,0,0,0,1.62526-2.64787l-.00015-37.42939a2.66554,2.66554,0,0,0-3.61038-2.50189A38.719,38.719,0,0,1,571.83305,346.24471Z"
        className="twilio"
      />
      <path
        d="M919.07784,208.71081a2.68257,2.68257,0,0,1,2.67465,2.67466V388.64021a2.68032,2.68032,0,0,1-2.67465,2.664H867.97053a2.67337,2.67337,0,0,1-2.67466-2.66395V211.38547a2.70017,2.70017,0,0,1,2.67466-2.67466Z"
        className="twilio"
      />
      <path
        d="M986.28557,208.71081a2.68257,2.68257,0,0,1,2.67465,2.67466v30.71574a2.6895,2.6895,0,0,1-2.67465,2.67465H935.17825a2.68945,2.68945,0,0,1-2.67465-2.67465V211.38547a2.68251,2.68251,0,0,1,2.67465-2.67466Z"
        className="twilio"
      />
      <path
        d="M986.28557,254.472a2.68256,2.68256,0,0,1,2.67465,2.67465v131.486a2.68255,2.68255,0,0,1-2.67465,2.67466H935.17825a2.67562,2.67562,0,0,1-2.67465-2.67466v-131.486a2.67563,2.67563,0,0,1,2.67465-2.67465Z"
        className="twilio"
      />
      <path
        d="M1070.85162,251.23677c-44.763,0-76.59139,32.83406-76.59139,71.12441v.50284c0,38.30106,31.58228,70.63229,76.09927,70.63229,44.763,0,76.60208-32.83407,76.60208-71.13513v-.49213c0-38.30106-31.593-70.63228-76.11-70.63228m21.88943,71.62725c0,12.93464-9.20082,23.62255-21.88943,23.62255-12.43175,0-22.38147-10.68791-22.38147-24.12539v-.49213c0-12.93464,9.20074-23.62255,21.88935-23.62255,12.43183,0,22.38155,10.68791,22.38155,24.11468Z"
        className="twilio"
      />
      <path
        d="M300.00329,150A150,150,0,1,0,450.00864,299.99465,150.00423,150.00423,0,0,0,300.00329,150m0,260.4686a110.46859,110.46859,0,1,1,110.474-110.47395,110.47247,110.47247,0,0,1-110.47395,110.474"
        className="twilio"
      />
      <path
        d="M306.17,262.64148A31.19182,31.19182,0,1,1,337.35645,293.828,31.18525,31.18525,0,0,1,306.17,262.64148"
        className="twilio"
      />
      <path
        d="M306.17,337.35637a31.19182,31.19182,0,1,1,31.18648,31.18648A31.18525,31.18525,0,0,1,306.17,337.35637"
        className="twilio"
      />
      <path
        d="M231.45616,337.35637a31.19182,31.19182,0,1,1,31.18647,31.18648,31.18524,31.18524,0,0,1-31.18647-31.18648"
        className="twilio"
      />
      <path
        d="M231.45616,262.64256A31.19182,31.19182,0,1,1,262.64263,293.829a31.18524,31.18524,0,0,1-31.18647-31.18648"
        className="twilio"
      />
    </svg>
  );
};

TwilioIcon.propTypes = {
  className: PropTypes.string,
  channelType: PropTypes.string,
  size: PropTypes.number,
  fill: PropTypes.string
};
